/**
 * Poseidon dashboard — application root.
 *
 * Routing uses `HashRouter` deliberately: the Rust daemon serves `dist/` as
 * plain static files, and a hash route keeps deep links working on refresh with
 * no server-side SPA fallback to configure. Switching to `BrowserRouter` is a
 * one-line change once the daemon serves `index.html` for unknown paths (the
 * README says which line).
 */
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { LoadingState, Panel } from './components/primitives';
import { API_MODE } from './config';
import { DataClockProvider } from './state/DataClock';
import { ServicesProvider, useServices } from './state/Services';
import { Backtests } from './pages/Backtests';
import { LiveOverview } from './pages/LiveOverview';
import { Login } from './pages/Login';
import { MarketMonitor } from './pages/MarketMonitor';
import { NotFound } from './pages/NotFound';
import { Operations } from './pages/Operations';
import { RiskCenter } from './pages/RiskCenter';
import { StrategyResearch } from './pages/StrategyResearch';
import { TradeDetail } from './pages/TradeDetail';
import { TradesAnalytics } from './pages/TradesAnalytics';

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * A render failure must not produce a blank screen: on a trading dashboard a
 * white page is indistinguishable from "flat and fine".
 */
class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console only. No error payload leaves the machine, and no secret is logged.
    console.error('Dashboard render error', error.message, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error !== null) {
      return (
        <div className="content">
          <Panel title="The dashboard failed to render" subtitle="client-side error">
            <p className="text-muted">
              A rendering error was caught so the rest of the application is not silently blank. The message below is shown as-is; no
              data was submitted anywhere.
            </p>
            <pre className="disclosure text-mono" style={{ whiteSpace: 'pre-wrap' }}>
              {this.state.error.message}
            </pre>
            <button type="button" className="btn" onClick={() => this.setState({ error: null })}>
              Try rendering again
            </button>
          </Panel>
        </div>
      );
    }
    return this.props.children;
  }
}

/** §3.1: no command endpoint is available before an operator session exists. */
function AuthenticatedApp(): JSX.Element {
  const { session, sessionLoading } = useServices();

  if (sessionLoading && session === null) {
    return (
      <div className="login-shell">
        <LoadingState label="Establishing operator session…" />
      </div>
    );
  }

  if (session === null || !session.authenticated) {
    return <Login />;
  }

  return <AppShell />;
}

export function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <DataClockProvider mode={API_MODE}>
        <ServicesProvider>
          <HashRouter>
            <Routes>
              <Route element={<AuthenticatedApp />}>
                <Route path="/" element={<LiveOverview />} />
                <Route path="/trades" element={<TradesAnalytics />} />
                <Route path="/trades/:completedTradeId" element={<TradeDetail />} />
                <Route path="/market" element={<MarketMonitor />} />
                <Route path="/risk" element={<RiskCenter />} />
                <Route path="/research" element={<StrategyResearch />} />
                <Route path="/backtests" element={<Backtests />} />
                <Route path="/operations" element={<Operations />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </HashRouter>
        </ServicesProvider>
      </DataClockProvider>
    </ErrorBoundary>
  );
}
